import React, { useState, useRef, useEffect } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { statusLabels, creditTypeLabels } from './utils';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  width?: string;
}

function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  width = 'w-56',
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={ref} className={`relative ${width}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 pr-10 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
      >
        <span className={value === 'all' ? 'text-gray-500' : 'text-gray-900'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
          >
            {options.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${
                  value === opt.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                <span>{opt.label}</span>
                {value === opt.value && <Check className="w-4 h-4 text-blue-600" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ApplicationListFiltersProps {
  searchTerm: string;
  filterStatus: string;
  filterType: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onTypeChange: (value: string) => void;
}

export default function ApplicationListFilters({
  searchTerm,
  filterStatus,
  filterType,
  onSearchChange,
  onStatusChange,
  onTypeChange,
}: ApplicationListFiltersProps) {
  const statusOptions = [
    { value: 'all', label: 'Semua Status' },
    ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
  ];

  const typeOptions = [
    { value: 'all', label: 'Semua Jenis' },
    ...Object.entries(creditTypeLabels).map(([value, label]) => ({ value, label })),
  ];

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari nomor aplikasi atau nama debitur..."
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <CustomSelect
          value={filterStatus}
          onChange={onStatusChange}
          options={statusOptions}
          placeholder="Status"
          width="w-full md:w-40"
        />
        <CustomSelect
          value={filterType}
          onChange={onTypeChange}
          options={typeOptions}
          placeholder="Jenis Kredit"
          width="w-full md:w-48"
        />
      </div>
    </div>
  );
}
